import { Component, OnInit, inject } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { combineLatest } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import * as ValidationPatterns from '../../configurations/validations';
import { EContactUsTopic, EFeatureToggles, EStoreTypes } from '../../configurations/common';
import { ConfigurationFacade } from '../../facades/configuration.facade';
import { ContactUsFacade } from '../../facades/contact-us.facade';
import { CustomerFacade } from '../../facades/customer.facade';
import { ISelectEvent } from '../../models/common.models';
import { ICustomerInfo } from '../../models/customer.models';
import { I18nService } from '../../services';
import { AppUtils } from '../../utils/app.utils';
import { LocalStorageUtils } from '../../utils/localStorage.utils';

@Component({
  selector: 'app-page-contact-us-general',
  templateUrl: './page-contact-us-general.component.html',
  styleUrls: ['./page-contact-us-general.component.scss'],
  standalone: false,
})
export class PageContactUsGeneralComponent implements OnInit {
  private formBuilder: UntypedFormBuilder = inject(UntypedFormBuilder);
  private activatedRoute: ActivatedRoute = inject(ActivatedRoute);
  private customerFacade: CustomerFacade = inject(CustomerFacade);
  private contactUsFacade: ContactUsFacade = inject(ContactUsFacade);
  private router: Router = inject(Router);
  private i18nService: I18nService = inject(I18nService);
  private configurationFacade: ConfigurationFacade = inject(ConfigurationFacade);

  contactUsForm!: UntypedFormGroup;
  showModalSubmit: boolean = false;
  maxLengthName: number = 35;
  maxLengthPhone: number = 20;
  maxLengthMessage: number = 500;
  currentRoute!: string;
  previousRoute: any = {
    path: '',
    params: {},
  };

  selectedTopic!: string;
  topicOptions: string[] = [];
  isJpStore: boolean = false;

  ngOnInit(): void {
    this.isJpStore = AppUtils.isStoreActive(EStoreTypes.JP);
    this.currentRoute = `/${this.activatedRoute.snapshot.url.join('/')}`;
    this.setPreviousRoute();
    this.setTopicOptions();

    if (!this.previousRoute.path || this.previousRoute.path === this.currentRoute) {
      this.setDefaultPreviousRoute();
    }

    this.activatedRoute.queryParams.pipe(take(1)).subscribe(params => {
      this.selectedTopic = params['selected-topic'] ?? '';
    });

    this.initializeForm();
  }

  /**
   * Sets the available contact us topic options based on the predefined equipment flow feature toggle.
   * When the predefined equipment flow is enabled, only 'General Request' and 'Different Role' topics are available.
   * When BP, only 'General Request', 'Missing Equipment' and 'Different Role' topics are available.
   * Otherwise, all topic options are available.
   */
  setTopicOptions(): void {
    combineLatest([
      this.configurationFacade.isFeatureEnabled(EFeatureToggles.PREDEFINED_EQUIPMENT_FLOW),
      this.customerFacade.isBusinessPartner(),
    ]).pipe(take(1))
      .subscribe(([isPredefinedEquipmentFlowEnabled, isBusinessPartner]) => {
        if (isPredefinedEquipmentFlowEnabled) {
          this.topicOptions = [
            EContactUsTopic.GENERAL_REQUEST,
            EContactUsTopic.DIFFERENT_ROLE,
          ];
        } else if (isBusinessPartner) {
          this.topicOptions = [
            EContactUsTopic.GENERAL_REQUEST,
            EContactUsTopic.MISSING_EQUIPMENT,
            EContactUsTopic.DIFFERENT_ROLE,
          ];
        } else {
          this.topicOptions = [
            EContactUsTopic.GENERAL_REQUEST,
            EContactUsTopic.WRONG_ADDRESS,
            EContactUsTopic.MISSING_EQUIPMENT,
            EContactUsTopic.DIFFERENT_ROLE,
          ];
        }
      });
  }

  closeModal(): void {
    this.resetForm();
    this.showModalSubmit = !this.showModalSubmit;
  }

  submitModalAndRedirectBack(): void {
    this.closeModal();
    this.router.navigate(
      [this.previousRoute.path],
      {queryParams: this.previousRoute.params},
    );
  }

  topicOptionLabel(option: string): string {
    return this.topicOptions.includes(option) ? `contact-us.topic-option.${option}` : 'contact-us.select-topic';
  }

  isSelectedTopic(option: string): boolean {
    return option === this.selectedTopic;
  }

  selectTopicOption(option: string): void {
    this.selectedTopic = option;
  }

  setContactUsFormValue(event: ISelectEvent): void {
    this.contactUsForm.patchValue({
      [event.key]: event.value,
    });
  }

  formIsInvalid(): boolean {
    return !this.selectedTopic || this.contactUsForm.invalid;
  }

  resetForm(): void {
    this.selectedTopic = '';
    this.initializeForm();
  }

  resetFormAndRedirectBack(): void {
    this.resetForm();
    this.router.navigate(
      [this.previousRoute.path],
      {queryParams: this.previousRoute.params},
    );
  }

  submitForm(): void {
    this.showModalSubmit = true;

    const formData: { data: { type: string; attributes: Record<string, unknown> } } = {
      data: {
        type: 'contact-us-general',
        attributes: {
          selectedTopic: this.selectedTopic,
          firstName: this.contactUsForm.value.firstName,
          lastName: this.contactUsForm.value.lastName,
          email: this.contactUsForm.value.email,
          phoneNumber: this.contactUsForm.value.phone,
          message: this.contactUsForm.value.message,
          receiveEmails: this.contactUsForm.value.receiveMails,
          hasConsented: this.contactUsForm.value.personalInformationConsent,
        },
      },
    };

    this.contactUsFacade.postContactUsGeneralData(formData)
      .pipe(take(1))
      .subscribe({
        next: () => {
        },
        error: () => {
        },
      });
  }

  private initializeForm(): void {
    this.contactUsForm = this.formBuilder.group({
      firstName: ['', [Validators.required, Validators.maxLength(this.maxLengthName), ValidationPatterns.onlyLetters]],
      lastName: ['', [Validators.required, Validators.maxLength(this.maxLengthName), ValidationPatterns.onlyLetters]],
      phone: ['', [Validators.maxLength(this.maxLengthPhone), ValidationPatterns.phonePattern]],
      email: ['', [Validators.required, Validators.email]],
      message: ['', [Validators.required, Validators.maxLength(this.maxLengthMessage), ValidationPatterns.noEmptySpaceOnTheBeginning]],
      receiveMails: [false, [Validators.requiredTrue]],
      personalInformationConsent: [false, [Validators.requiredTrue]],
    });

    this.selectCustomerInfo();
  }

  private selectCustomerInfo(): void {
    this.customerFacade.selectMyProfileInformation().pipe(
      filter((state): state is ICustomerInfo => state !== null),
      take(1),
    ).subscribe(state => {
      this.contactUsForm.patchValue({
        firstName: state.firstName || '',
        lastName: state.lastName || '',
        email: state.email || '',
      });
    });
  }

  private setPreviousRoute(): void {
    this.previousRoute.path = LocalStorageUtils.getKeyValue('previousPage');
    this.previousRoute.params = this.router.parseUrl(this.previousRoute.path).queryParams;

    this.previousRoute.path = this.previousRoute.path.indexOf('?') !== -1
      ? this.previousRoute.path.substring(0, this.previousRoute.path.indexOf('?'))
      : this.previousRoute.path;
  }

  private setDefaultPreviousRoute(): void {
    this.previousRoute.path = `/${this.i18nService.getCurrentLocale()}`;
    this.previousRoute.params = {};
  }
}
